import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { 
  Zap, Mail, MessageSquare, 
  Clock, Database, Bot,
  Webhook, ArrowRight,
  Check, Power, Filter, Timer, Trash2
} from 'lucide-react';

const icons: Record<string, any> = {
  trigger: Webhook,
  action: Zap,
  sms: MessageSquare,
  email: Mail,
  delay: Clock,
  timer: Timer,
  ai: Bot,
  database: Database,
  condition: Filter
};

const colors: Record<string, string> = {
  trigger: 'text-green-500 bg-green-500/10 border-green-500/20',
  action: 'text-[var(--t-primary)] bg-[var(--t-primary-dim)] border-[var(--t-primary)]/20',
  sms: 'text-blue-500 bg-blue-500/10 border-blue-500/20',
  email: 'text-indigo-500 bg-indigo-500/10 border-indigo-500/20',
  delay: 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20',
  timer: 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20',
  ai: 'text-purple-500 bg-purple-500/10 border-purple-500/20',
  database: 'text-cyan-500 bg-cyan-500/10 border-cyan-500/20',
  condition: 'text-orange-500 bg-orange-500/10 border-orange-500/20'
};

export const AutomationNode = memo(({ id, data }: any) => {
  const Icon = icons[data.type] || Zap;
  const colorClass = colors[data.type] || 'text-[var(--t-text)] bg-[var(--t-surface-hover)] border-[var(--t-border)]';
  const [editingLabel, setEditingLabel] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [label, setLabel] = useState(data.label || '');
  const [desc, setDesc] = useState(data.description || '');
  const [enabled, setEnabled] = useState(data.enabled !== false);
  const { setNodes } = useReactFlow();
  
  useEffect(() => {
    console.log(`[AutomationNode] Mounted: ${id}`, data);
  }, [id, data]);

  const commitChanges = useCallback((newLabel?: string, newDesc?: string, newEnabled?: boolean) => {
    setNodes(nds => nds.map(n => n.id === id ? {
      ...n, data: { 
        ...n.data, 
        label: newLabel ?? label, 
        description: newDesc ?? desc,
        enabled: newEnabled ?? enabled
      }
    } : n));
  }, [id, label, desc, enabled, setNodes]);

  return (
    <div 
      className={`group relative min-w-[250px] min-h-[100px] rounded-2xl shadow-xl p-4 transition-all hover:border-[var(--t-primary)] hover:shadow-[var(--t-primary-dim)] ${!enabled ? 'opacity-50' : ''}`}
      style={{ 
        backgroundColor: 'var(--t-surface)', 
        border: '1px solid var(--t-border)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
      onDoubleClick={() => {
        // Trigger select and settings via parent or store if needed
        // For now, React Flow onNodeClick usually handles this
      }}
    >
      {/* Delete Button */}
      <button 
        onClick={(e) => {
          e.stopPropagation();
          setNodes(nds => nds.filter(n => n.id !== id));
        }}
        className="absolute top-2 right-2 p-1.5 bg-red-500/10 text-red-500 rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-red-500 hover:text-white z-[60]"
        title="Delete Node"
      >
        <Trash2 size={12} />
      </button>
      <Handle 
        type="target" 
        position={Position.Top} 
        className="!w-4 !h-4 !bg-[var(--t-primary)] border-2 border-[var(--t-bg)] hover:!scale-125 transition-transform cursor-crosshair z-50" 
      />
      
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${colorClass} ${data.status === 'running' ? 'animate-pulse scale-110 shadow-[0_0_15px_rgba(var(--t-primary-rgb),0.5)]' : ''}`}>
          {data.status === 'success' ? <Check className="text-green-500" size={20} /> : <Icon size={20} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">{data.type}</p>
          {editingLabel ? (
            <input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={() => { setEditingLabel(false); commitChanges(label); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { setEditingLabel(false); commitChanges(label); } }}
              className="text-sm font-bold text-[var(--t-text)] bg-[var(--t-surface-hover)] border border-[var(--t-primary)]/50 rounded px-1 py-0.5 w-full outline-none focus:ring-1 focus:ring-[var(--t-primary)]"
            />
          ) : (
            <h4 
              className="text-sm font-bold text-[var(--t-text)] truncate cursor-text hover:text-[var(--t-primary)] transition-colors" 
              onDoubleClick={() => setEditingLabel(true)}
              title="Double-click to edit"
            >
              {data.label}
            </h4>
          )}
        </div>
      </div>

      {editingDesc ? (
        <textarea
          autoFocus
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          onBlur={() => { setEditingDesc(false); commitChanges(undefined, desc); }}
          className="mt-3 text-[10px] text-[var(--t-text)] bg-[var(--t-surface-hover)] border border-[var(--t-primary)]/50 rounded px-1.5 py-1 w-full outline-none resize-none leading-relaxed focus:ring-1 focus:ring-[var(--t-primary)]"
          rows={2}
        />
      ) : (
        data.description && (
          <p 
            className="mt-3 text-[10px] text-[var(--t-text-muted)] leading-relaxed cursor-text hover:text-[var(--t-text)] transition-colors"
            onDoubleClick={() => setEditingDesc(true)}
            title="Double-click to edit"
          >
            {data.description}
          </p>
        )
      )}

      <div className="mt-4 pt-3 border-t border-[var(--t-border)] flex items-center justify-between">
        <button 
          onClick={() => { const next = !enabled; setEnabled(next); commitChanges(undefined, undefined, next); }}
          className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
            enabled 
              ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
              : 'bg-red-500/10 text-red-400 border border-red-500/20'
          }`}
          title={enabled ? 'Click to disable' : 'Click to enable'}
        >
          {enabled ? <Check size={10} /> : <Power size={10} />}
          {enabled ? 'Active' : 'Off'}
        </button>
        <ArrowRight size={14} className="text-[var(--t-text-muted)] group-hover:text-[var(--t-primary)] transition-colors" />
      </div>

      <Handle 
        type="source" 
        position={Position.Bottom} 
        className="!w-4 !h-4 !bg-[var(--t-primary)] border-2 border-[var(--t-bg)] hover:!scale-125 transition-transform cursor-crosshair z-50" 
      />
    </div>
  );
});
